/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { CloseIcon } from '@chakra-ui/icons';
import {
    HStack,
    IconButton,
    Modal,
    ModalOverlay,
    Skeleton,
    Stack,
} from '@chakra-ui/react';
import {
    Button,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Text,
} from 'components/v4';

import walkthroughIds from '../../../accounts/admins/walkthroughIds';

import { useTenantDomainForm } from './useTenantDomainForm';
import useGetSSOProvider from './queries/useGetSSOProvider';
import useUpdateSSOProvider from './queries/useUpdateSSOProvider';

import { BUTTON_WIDTH } from './constants';

import SSOStatusCellRenderer from './SSOStatusCellRenderer';
import MicrosoftProviderPreview from './components/MicrosoftProviderPreview';
import TenantDomainFormBody from './components/TenantDomainFormBody';

export default function EditMicrosoftSSO({ isOpen, onClose, providerId }) {
    const { darkMode } = useSelector((state) => state.settings);
    const { ufBackBtn, ufSubmitBtn } = walkthroughIds.adminSSO;

    const queryClient = useQueryClient();
    const form = useTenantDomainForm();
    const updateProviderMutation = useUpdateSSOProvider();

    const { data: providerData, isFetching } = useGetSSOProvider(providerId, {
        enabled: isOpen && !!providerId,
    });

    // Populate form whenever fresh provider data arrives
    useEffect(() => {
        if (!providerData || !isOpen) return;
        form.populate(
            providerData.tenantIds,
            providerData.emailDomains,
            providerData.isBoth,
        );
    }, [providerData, isOpen]);

    const handleSubmit = () => {
        updateProviderMutation.mutate(
            {
                ...providerData,
                isBoth: form.showDomainRestriction,
                tenantIds: form.tenantIds,
                emailDomains: form.domains,
            },
            { onSuccess: onClose },
        );
    };

    const handleReset = () => {
        form.reset();
        queryClient.removeQueries({ queryKey: ['ssoProvider', providerId] });
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleReset} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader pb={0}>
                    <HStack align="start" justify="space-between">
                        <Stack spacing={3} flex={1}>
                            <HStack spacing={3} align="center">
                                <Text fontWeight="bold" fontSize="xl">
                                    Edit Identity Provider — Microsoft
                                </Text>
                                {!isFetching && (
                                    <SSOStatusCellRenderer
                                        status={
                                            providerData?.isVerified
                                                ? 'verified'
                                                : 'verify'
                                        }
                                    />
                                )}
                            </HStack>
                            <MicrosoftProviderPreview borderRadius="md" />
                        </Stack>
                        <IconButton
                            icon={<CloseIcon boxSize={3} />}
                            onClick={handleReset}
                            variant="ghost"
                            size="sm"
                            aria-label="Close"
                        />
                    </HStack>
                </ModalHeader>

                <ModalBody>
                    {isFetching ? (
                        <Stack spacing={4} mt={6}>
                            <Skeleton h={8} />
                            <Skeleton h={5} w="30%" />
                            <Skeleton h="140px" />
                            <Skeleton h={16} borderRadius="lg" />
                        </Stack>
                    ) : (
                        <TenantDomainFormBody
                            {...form}
                            darkMode={darkMode}
                            titlePrefix="sso-edit"
                            columnFlex={2}
                            emptyGridHeight="140px"
                        />
                    )}
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3} w="full" justify="flex-end">
                        <Button
                            data-walkthroughid={ufBackBtn}
                            variant="outline"
                            w={BUTTON_WIDTH}
                            onClick={handleReset}>
                            Cancel
                        </Button>
                        <Button
                            data-walkthroughid={ufSubmitBtn}
                            isLoading={updateProviderMutation.isPending}
                            isDisabled={!form.isFormValid || isFetching}
                            w={BUTTON_WIDTH}
                            onClick={handleSubmit}>
                            Update
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
